import { meGetGamificationAccountTransactions } from '../../features/queries/meGetGamificationAccountTransactions';
import { useInfiniteQ } from '../../lib/query';
import { __, dummyArray } from '../../utils';
import TransactionsDetail from './TransactionDetail';
import TransactionsListItem from './TransactionsListItem';
import TransactionsListItemSkeleton from './TransactionsListItemSkeleton';
import { CircularProgress, List, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useSearchParams } from 'react-router';

export const TRANSACTIONS_LIST_LIMIT = 10;

type Props = {
  accountId: number;
  hasTabs?: boolean;
  initModalTransactionId?: number;
};

const TransactionsList: React.FC<Props> = ({
  accountId,
  initModalTransactionId,
  hasTabs = false,
}) => {
  const [modalTransactionId, setModalTransactionId] = React.useState<
    number | null
  >(initModalTransactionId || null);
  const serachParams = useSearchParams()[0];
  const transactionIdParam = serachParams.get('transactionId');

  React.useEffect(() => {
    if (transactionIdParam) {
      const transactionId = parseInt(transactionIdParam, 10);
      if (!isNaN(transactionId)) {
        setModalTransactionId(transactionId);
      }
    }
  }, [transactionIdParam]);

  const loaderRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const {
    data: transactions,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQ(
    meGetGamificationAccountTransactions,
    {
      accountId,
      limit: TRANSACTIONS_LIST_LIMIT,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage || !lastPage.nextCursor) return undefined;
        return lastPage.nextCursor;
      },
    },
  );

  React.useEffect(() => {
    if (isLoading) return;
    const loader = loaderRef.current;
    if (!loader) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: listRef.current, threshold: 0.1 },
    );
    observer.observe(loader);

    return () => observer.disconnect();
  }, [fetchNextPage, isLoading]);

  const formattedTransactions = React.useMemo(() => {
    return Array.isArray(transactions?.pages)
      ? transactions.pages.flatMap((page) => page.transactions)
      : [];
  }, [transactions]);

  return (
    <>
      <List
        ref={listRef}
        sx={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          mt: hasTabs ? 5 : 0,
          p: 0,
          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {isLoading ? (
          dummyArray(TRANSACTIONS_LIST_LIMIT).map((_, index) => (
            <TransactionsListItemSkeleton key={index} />
          ))
        ) : formattedTransactions.length > 0 ? (
          formattedTransactions.map((transaction) => {
            return (
              <TransactionsListItem
                key={transaction.transaction_id}
                transaction={transaction}
                onOpen={() => {
                  setModalTransactionId(transaction.transaction_id);
                  const params = new URLSearchParams(serachParams.toString());
                  params.set(
                    'transactionId',
                    transaction.transaction_id.toString(),
                  );
                  const newSearch = params.toString();
                  const newUrl =
                    window.location.pathname +
                    (newSearch ? `?${newSearch}` : '');
                  window.history.replaceState({}, '', newUrl);
                }}
              />
            );
          })
        ) : (
          <Typography p={3} align='center' variant='body2'>
            {__('Žiadne transakcie')}
          </Typography>
        )}
        {hasNextPage && (
          <Stack
            ref={loaderRef}
            sx={{
              minHeight: 40,
              height: 40,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Stack>
        )}
      </List>
      <TransactionsDetail
        openTransactionId={modalTransactionId}
        setOpenTransactionId={setModalTransactionId}
        accountId={accountId}
        onClose={() => {
          const params = new URLSearchParams(serachParams.toString());
          params.delete('transactionId');
          const newSearch = params.toString();
          const newUrl =
            window.location.pathname + (newSearch ? `?${newSearch}` : '');
          window.history.replaceState({}, '', newUrl);
        }}
      />
    </>
  );
};

export default TransactionsList;
